import { Edges, useCursor } from '@react-three/drei';
import { useCallback, useEffect, useRef, useState } from 'react';
import { invokeEvent } from 'shared-base';

type TiledFloorProps = {
  tilesX?: number;
  tilesY?: number;
  tileSize?: number;
  tileHeight?: number;
  onDragStateChange?: (dragging: boolean) => void;
  onTileAreaChange: (area?: Area) => void;
};

type TileCoord = { x: number; y: number };
type Area = { from: TileCoord; to: TileCoord };

function getTileKey(x: number, y: number) {
  return `${x}-${y}`;
}

function isWithinArea(coord: TileCoord, area: Area): boolean {
  const minX = Math.min(area.from.x, area.to.x);
  const maxX = Math.max(area.from.x, area.to.x);
  const minY = Math.min(area.from.y, area.to.y);
  const maxY = Math.max(area.from.y, area.to.y);
  return coord.x >= minX && coord.x <= maxX && coord.y >= minY && coord.y <= maxY;
}

const DELAY = 8000; // 10 seconds

export function TiledFloor(props: TiledFloorProps) {
  const { tilesX = 6, tilesY = 6, tileSize = 1, tileHeight = 0.25 } = props;
  const [hovered, setHovered] = useState<string | null>(null);
  const [selectedArea, setSelectedArea] = useState<Area | undefined>(undefined);
  const [dragArea, setDragArea] = useState<Area | null>(null);
  const dragStart = useRef<TileCoord | null>(null);

  useCursor(!!hovered);

  // Clear selection after 5 seconds
  useEffect(() => {
    if (!selectedArea) return;
    props.onTileAreaChange(selectedArea);
    const timeout = setTimeout(() => setSelectedArea(undefined), DELAY);
    return () => clearTimeout(timeout);
  }, [selectedArea]);

  const onPointerDown = useCallback((x: number, y: number) => {
    dragStart.current = { x, y };
    setDragArea({ from: { x, y }, to: { x, y } });
  }, []);

  const onPointerEnter = useCallback((x: number, y: number) => {
    if (dragStart.current) {
      setDragArea({ from: dragStart.current, to: { x, y } });
    }
  }, []);

  const onPointerUp = useCallback((x: number, y: number) => {
    if (dragStart.current) {
      const area = { from: dragStart.current, to: { x, y } };
      setSelectedArea(area);
      dragStart.current = null;
      setDragArea(null);
    } else {
      const coord = { x, y };
      const singleTileArea = { from: coord, to: coord };
      setSelectedArea(singleTileArea);
    }
  }, []);

  const tiles: JSX.Element[] = [];

  for (let x = 0; x < tilesX; x++) {
    for (let y = 0; y < tilesY; y++) {
      const key = getTileKey(x, y);
      const isHovered = hovered === key;
      const isSelected = selectedArea && isWithinArea({ x, y }, selectedArea);
      const isDragging = dragArea && isWithinArea({ x, y }, dragArea);

      let color = '#233c88';
      if (isDragging) color = '#2e60cc';
      else if (isSelected) color = '#3756b1';
      else if (isHovered) color = '#3756b1';

      tiles.push(
        <mesh
          castShadow
          receiveShadow
          key={key}
          position={[
            (x - tilesX / 2) * tileSize + tileSize / 2,
            -tileHeight / 2 - 0.1,
            (y - tilesY / 2) * tileSize + tileSize / 2,
          ]}
          onPointerOver={(e) => {
            e.stopPropagation();
            setHovered(key);
            onPointerEnter(x, y);
            invokeEvent('REALTIME', { verb: 'tileHover', tileId: key });
          }}
          onPointerOut={(e) => {
            e.stopPropagation();
            setHovered(null);
          }}
          onPointerDown={(e) => {
            e.stopPropagation();
            onPointerDown(x, y);
            props.onDragStateChange?.(true); // 🚩 Start drag
          }}
          onPointerUp={(e) => {
            e.stopPropagation();
            onPointerUp(x, y);
            props.onDragStateChange?.(false); // ✅ End drag
          }}
        >
          <boxGeometry args={[tileSize, tileHeight, tileSize]} />
          <Edges scale={1.01} color='#3756b1' />
          <meshStandardMaterial color={color} />
        </mesh>
      );
    }
  }

  return <>{tiles}</>;
}
